import { ChatMessage } from 'helpers'
import { useEffect, useRef, useState } from 'react'
import { getChatMessage, getLastMessage } from 'services/chat'
import {
  ChatNotifications,
  useFirebaseListener
} from 'services/context/firebaseListener'
import UniqueSet from 'utils/structures'

const LIMIT = 10

let timeout: NodeJS.Timeout

const useMessageList = (chatId: string) => {
  const lastItemRef = useRef<HTMLDivElement[]>([])
  const [messageSet, setMessageSet] = useState<UniqueSet<ChatMessage>>(
    new UniqueSet()
  )
  const [lastUpdated, setLastUpdated] = useState<string>()
  const { chatNotifications, readMessage } = useFirebaseListener()

  const subheaderRefs = useRef<{ [key: string]: HTMLElement | null }>({})
  const lastMessageRefs = useRef<{ [key: string]: HTMLElement | null }>({})
  const [currentHeader, setCurrentHeader] = useState<string>('')

  const [isVisible, setIsVisible] = useState(false)
  const scrollContainerRef = useRef<HTMLUListElement>(null)

  const setReadMessage = () => {
    if (readMessage) readMessage(chatId)
  }

  const getAllData = async () => {
    const response = await getChatMessage(chatId, {
      offset: messageSet.size,
      limit: LIMIT
    })

    if (response.success && response.data!.length > 0) {
      const data = response.data!.map(item => new ChatMessage(item))
      const newSet = new UniqueSet<ChatMessage>([
        ...messageSet.getItems(),
        ...Array.from(data)
      ])
      messageSet.clear()
      setMessageSet(newSet)
    }
  }

  const fetchMessageData = async () => {
    if (messageSet.size == 0) {
      await getAllData()
    } else {
      messageSet.getItems()
      const response = await getLastMessage(chatId, {
        after_datetime: messageSet.first?.created_at
      })

      if (response.success) {
        const data = response.data!.map(item => new ChatMessage(item))
        const newSet = new UniqueSet<ChatMessage>(data)
        newSet.addAll(messageSet.getItems())
        messageSet.clear()
        setMessageSet(newSet)
      }
    }

    setReadMessage()
  }

  const initialSettings = () => {
    const notifications: ChatNotifications =
      chatNotifications ?? ({} as ChatNotifications)
    if (notifications[chatId ?? '']?.lastUpdated !== lastUpdated)
      setLastUpdated(notifications[chatId ?? '']?.lastUpdated)
  }

  // On scroll, show fixed header
  const handleScroll: React.UIEventHandler<HTMLUListElement> = event => {
    clearTimeout(timeout)
    if (!isVisible) {
      setIsVisible(true)
    }

    timeout = setTimeout(() => {
      setIsVisible(false)
    }, 800)
  }

  // To set header text
  const onLastMessage = () => {
    const subheaders = Object.entries(subheaderRefs.current)
    const scroll = (scrollContainerRef.current?.scrollTop ?? 0) + 36

    for (const [key, target] of subheaders) {
      if ((target?.offsetTop ?? scroll) < scroll) {
        setCurrentHeader(key)
        break
      }
    }
  }

  useEffect(initialSettings, [chatNotifications])

  useEffect(() => {
    messageSet.clear()
    setIsVisible(false)
    setCurrentHeader('')
    setLastUpdated(undefined)
    initialSettings()
  }, [chatId])

  useEffect(() => {
    if (lastUpdated !== undefined) {
      fetchMessageData()
    }
  }, [lastUpdated])

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          getAllData()
        }
      },
      { threshold: 1.0 }
    )
    if (lastItemRef.current[0]) {
      observer.observe(lastItemRef.current[0])
    }

    // To change text in fixed header on scroll down or scroll up
    const observerLastMessage = new IntersectionObserver(onLastMessage, {
      root: scrollContainerRef.current,
      threshold: 0
    })

    Object.values(lastMessageRefs.current).forEach(el => {
      if (el) observerLastMessage.observe(el)
    })

    return () => {
      observer.disconnect()
      observerLastMessage.disconnect()
    }
  }, [messageSet])

  const renderSection = () => {
    const list: Array<ChatMessage | string> = []

    messageSet.getItems().forEach((current, index, allData) => {
      list.push(current)
      if (allData.length > index + 1) {
        const next = allData[index + 1].date

        if (current.date != next) {
          list.push(current.date)
        }
      } else if (allData.length == index + 1) {
        list.push(current.date)
      }
    })

    return list
  }

  return {
    messageList: renderSection(),
    currentHeader: isVisible ? currentHeader : '',
    lastItemRef,
    lastMessageRefs,
    handleScroll,
    scrollContainerRef,
    subheaderRefs
  }
}

export default useMessageList
